/**
 * Tope de registros de cualquier descarga Excel / nómina.
 *
 * Es el mismo valor que aplican el backend y cyber-service, para no saturar el
 * Function host armando archivos gigantes:
 *
 * - Sin registros seleccionados se descargan los primeros 5000 (en el orden de
 *   la tabla) y se avisa a la persona. El backend informa el recorte con los
 *   headers X-Export-* (leerRecorteDescarga).
 * - Con una selección de más de 5000 la descarga se rechaza: ahí la persona
 *   eligió explícitamente qué bajar. El frontend corta antes cuando conoce el
 *   tamaño de la selección; si no, el backend responde 422 con el mensaje
 *   (leerMensajeLimiteDescarga).
 */
export const MAX_REGISTROS_DESCARGA = 5000;

const CODIGO_LIMITE_EXCEDIDO = "EXPORT_LIMIT_EXCEEDED";

const formatear = (valor: number): string => valor.toLocaleString("es-CL");

export const TITULO_LIMITE_DESCARGA = "Descarga demasiado grande";

/** Selección mayor al tope (se rechaza). */
export const mensajeLimiteDescarga = (total?: number): string => {
  const detalle =
    total !== undefined && total > MAX_REGISTROS_DESCARGA
      ? ` (seleccionaste ${formatear(total)})`
      : "";
  return `La descarga supera el máximo de ${formatear(MAX_REGISTROS_DESCARGA)} registros${detalle}. Selecciona menos registros.`;
};

export const excedeLimiteDescarga = (total: number | undefined | null): boolean =>
  typeof total === "number" && total > MAX_REGISTROS_DESCARGA;

export const TITULO_DESCARGA_SIN_SELECCION = "Descarga sin registros seleccionados";

/**
 * Aviso previo a toda descarga sin selección (Descargar Nómina, Excel Master,
 * Inhabilitados, Procesos activos, Postulaciones con prefiltro).
 */
export const AVISO_DESCARGA_SIN_SELECCION = `Como no hay registros seleccionados, se descargarán los primeros ${formatear(
  MAX_REGISTROS_DESCARGA
)} registros.`;

/** Aviso posterior a una descarga sin selección que se recortó. */
export const mensajeDescargaRecortada = (total?: number): string => {
  const detalle =
    total !== undefined && total > MAX_REGISTROS_DESCARGA
      ? ` de ${formatear(total)}`
      : "";
  return `Como no había registros seleccionados, se descargaron los primeros ${formatear(
    MAX_REGISTROS_DESCARGA
  )} registros${detalle}. Selecciona registros o aplica filtros para descargar otros.`;
};

export interface IRecorteDescarga {
  recortado: boolean;
  /** Total sin recortar, cuando el backend lo conoce. */
  total?: number;
}

/**
 * Lee los headers X-Export-* de la respuesta (axios entrega un objeto con
 * claves en minúscula; fetch, un Headers).
 */
export const leerRecorteDescarga = (headers: any): IRecorteDescarga => {
  const leer = (nombre: string): string | null | undefined =>
    typeof headers?.get === "function"
      ? headers.get(nombre)
      : headers?.[nombre.toLowerCase()] ?? headers?.[nombre];
  const recortado = leer("X-Export-Truncated") === "true";
  const totalCrudo = Number(leer("X-Export-Total"));
  return {
    recortado,
    total: recortado && Number.isFinite(totalCrudo) && totalCrudo > 0 ? totalCrudo : undefined,
  };
};

/**
 * Si el error de axios es el rechazo por tope (422), devuelve el mensaje para
 * mostrar; si no, undefined. Con responseType "blob" el cuerpo llega como Blob.
 */
export const leerMensajeLimiteDescarga = async (
  error: any
): Promise<string | undefined> => {
  const response = error?.response;
  if (response?.status !== 422) return undefined;

  let cuerpo: any = response.data;
  try {
    if (cuerpo && typeof cuerpo.text === "function") cuerpo = await cuerpo.text();
    if (typeof cuerpo === "string") cuerpo = JSON.parse(cuerpo);
  } catch (parseError) {
    cuerpo = undefined;
  }

  if (cuerpo?.code !== CODIGO_LIMITE_EXCEDIDO) return undefined;
  return cuerpo.message ?? mensajeLimiteDescarga(cuerpo.total ?? undefined);
};
